import * as THREE from 'three';
import { ATMO } from '../config/tuning';
import { MISSILE } from '../config/weapons';

/**
 * Proportional navigation (SPEC §3.6.2) — the guidance law the whole evasion model is built on.
 *
 * ```
 * R      = targetPos − missilePos                 // range vector
 * V_rel  = targetVel − missileVel                 // relative velocity
 * Ω      = (R × V_rel) / (R · R)                  // line-of-sight rotation rate
 * V_c    = −(R · V_rel) / |R|                     // closing speed
 * a_cmd  = N · V_c · (Ω × normalise(missileVel))  // commanded lateral acceleration
 * ```
 *
 * The behavioural consequence is the entire skill of the game: **a missile on a constant-bearing
 * intercept has zero LOS rate and needs no manoeuvre.** Forcing the LOS rate up — by turning
 * hard, late, and perpendicular to the missile — forces it to pull g, and pulling g costs it
 * speed it cannot get back (see the induced drag term in Missile.ts). Every one of the five
 * evasion techniques in SPEC §3.6.5 is ultimately a way of manipulating this one equation.
 *
 * Everything here is pure and writes into out-parameters. Units: metres, m/s, m/s².
 */

// Module-scope scratch. Guidance runs per missile per tick and must not allocate (SPEC §4.3).
const omega = new THREE.Vector3();
const missileDirection = new THREE.Vector3();
const approach = new THREE.Vector3();

/**
 * Closing speed along the line of sight, m/s. Positive means the range is shrinking.
 *
 * This is the number the missile-warning tick rate is driven from, and the one that decides when
 * the terminal phase begins — so it is exported rather than buried inside the guidance step.
 */
export function closingSpeedMs(
  relativePositionM: THREE.Vector3,
  relativeVelocityMs: THREE.Vector3,
): number {
  const rangeM = relativePositionM.length();
  if (rangeM < 1e-6) return 0;
  return -relativePositionM.dot(relativeVelocityMs) / rangeM;
}

/**
 * Line-of-sight rotation rate vector, rad/s: `Ω = (R × V_rel) / (R · R)`.
 *
 * Its magnitude is what the missile is trying to drive to zero. A player watching a missile that
 * holds a constant bearing against the canopy is watching this number be zero, which is why the
 * old fighter-pilot rule — *if it isn't moving in the windscreen, it's going to hit you* — is
 * literally a reading of this equation.
 */
export function lineOfSightRate(
  relativePositionM: THREE.Vector3,
  relativeVelocityMs: THREE.Vector3,
  out: THREE.Vector3,
): THREE.Vector3 {
  const rangeSq = relativePositionM.lengthSq();
  if (rangeSq < 1e-9) return out.set(0, 0, 0);
  return out.crossVectors(relativePositionM, relativeVelocityMs).divideScalar(rangeSq);
}

/**
 * Commanded lateral acceleration from proportional navigation, m/s², written into `out`.
 *
 * `navConstant` is 3.5 for modern seekers and 3.0 for older ones (SPEC §3.6.2). The command is
 * perpendicular to the missile's velocity by construction — PN steers, it does not accelerate
 * along the flight path — which is why the result can be fed straight into the induced-drag term
 * as the g the airframe is being asked to pull.
 *
 * Returns a zero command when the geometry is degenerate (co-located, or opening rather than
 * closing): a missile that has already been beaten should fly straight, not thrash.
 */
export function proportionalNavigation(
  relativePositionM: THREE.Vector3,
  relativeVelocityMs: THREE.Vector3,
  missileVelocityMs: THREE.Vector3,
  navConstant: number,
  out: THREE.Vector3,
): THREE.Vector3 {
  const speed = missileVelocityMs.length();
  if (speed < 1e-6) return out.set(0, 0, 0);

  const closing = closingSpeedMs(relativePositionM, relativeVelocityMs);
  if (closing <= 0) return out.set(0, 0, 0);

  lineOfSightRate(relativePositionM, relativeVelocityMs, omega);
  missileDirection.copy(missileVelocityMs).divideScalar(speed);

  // a = N · V_c · (Ω × v̂). The cross product is what makes the command perpendicular to flight.
  return out.crossVectors(omega, missileDirection).multiplyScalar(navConstant * closing);
}

/**
 * Add the gravity bias, m/s², in place.
 *
 * A missile that did not compensate would sag below its intercept and consistently pass beneath
 * a level target. Compensating *fully* would be worse: the trajectory would be perfectly flat and
 * a diving target would gain nothing from the vertical geometry, removing a whole dimension from
 * the evasion problem. `gravityBiasFactor` sits between the two.
 */
export function addGravityBias(command: THREE.Vector3): THREE.Vector3 {
  command.y += ATMO.G * MISSILE.gravityBiasFactor;
  return command;
}

/**
 * Clamp a commanded acceleration to the airframe's g limit, in place. Returns the magnitude
 * actually commanded, m/s² — which is what the induced drag penalty is charged against.
 *
 * Clamping the *magnitude* rather than each axis matters: a per-axis clamp would silently rotate
 * the command away from the direction the seeker asked for, and the missile would miss for
 * reasons no amount of staring at the guidance equation would explain.
 */
export function limitAcceleration(command: THREE.Vector3, maxG: number): number {
  const limit = maxG * ATMO.G;
  const magnitude = command.length();
  if (magnitude > limit) {
    command.multiplyScalar(limit / magnitude);
    return limit;
  }
  return magnitude;
}

/**
 * Component of `command` perpendicular to the flight path, m/s², written into `out`.
 *
 * Only the lateral component bends the trajectory, and only the lateral component costs induced
 * drag. The gravity bias adds a vertical term that is partly along the velocity for a climbing or
 * diving missile, and charging drag for that part would penalise the missile for holding altitude.
 */
export function lateralComponent(
  command: THREE.Vector3,
  missileVelocityMs: THREE.Vector3,
  out: THREE.Vector3,
): number {
  const speed = missileVelocityMs.length();
  if (speed < 1e-6) {
    out.copy(command);
    return command.length();
  }
  missileDirection.copy(missileVelocityMs).divideScalar(speed);
  const along = command.dot(missileDirection);
  out.copy(command).addScaledVector(missileDirection, -along);
  return out.length();
}

/**
 * Time to closest approach along the current relative motion, seconds, and the miss distance
 * that would result. Used by the terminal-phase test and by the debrief at M10.
 *
 * Returns a negative time when the two are already separating.
 */
export function timeToClosestApproachS(
  relativePositionM: THREE.Vector3,
  relativeVelocityMs: THREE.Vector3,
): number {
  const speedSq = relativeVelocityMs.lengthSq();
  if (speedSq < 1e-9) return -1;
  return -relativePositionM.dot(relativeVelocityMs) / speedSq;
}

/**
 * Miss distance at closest approach over a segment of duration `dtS`, metres.
 *
 * **This is the sub-tick proximity fuse of SPEC §4.4 and the M6 notes.** At 800 m/s of closure a
 * 120 Hz tick covers 6.7 m, against a fuse radius of 6–12 m: testing only the endpoints would
 * step straight through the fuse envelope and miss detonations entirely. Solving for the closest
 * approach *within* the tick is not an optimisation, it is the difference between a fuse that
 * works and one that fires at random.
 *
 * `outFraction` receives where in the tick the closest approach happened, 0–1, so the caller can
 * place the detonation at the right point rather than at the end of the step.
 */
export function closestApproachM(
  relativePositionM: THREE.Vector3,
  relativeVelocityMs: THREE.Vector3,
  dtS: number,
  outFraction: { value: number },
): number {
  const timeS = timeToClosestApproachS(relativePositionM, relativeVelocityMs);
  const clamped = timeS < 0 ? 0 : timeS > dtS ? dtS : timeS;
  outFraction.value = dtS > 0 ? clamped / dtS : 0;

  approach.copy(relativePositionM).addScaledVector(relativeVelocityMs, clamped);
  return approach.length();
}
